<template>
  <div class="a-title-and-artist">
    <PageTitle v-if="pageTitle" :class="classes" :page-title="pageTitle" />
    <div v-if="creationDate" class="a-title-and-artist__creation-date">
      {{ creationDate }}
    </div>
    <!--
      if the definition of complexObjectTypes that are considered fragments changes,
      this logic must also be updated in the humanMadeObject page
    -->
    <div
      v-if="showFragmentPartsLink"
      class="a-title-and-artist__view-fragments"
    >
      {{ fragmentPartsPrefix }}
      <GuiButton
        name="partsButton"
        class="a-title-and-artist__fragment-parts-link"
        @click="scrollToFragmentParts"
      >
        {{ fragmentPartsLink }}
      </GuiButton>
    </div>
    <div class="a-title-and-artist__subtitle">
      <DelimitedFieldValues
        v-if="artist"
        class="a-title-and-artist__subtitle-artist"
        :values="sliceArrayOfArtistsForCaption"
        delimiter=", "
      />
      <span v-if="shouldSliceArrayOfArtistsForCaption">, </span>
      <GuiLink
        v-if="shouldSliceArrayOfArtistsForCaption"
        class="a-title-and-artist__subtitle-more-artists"
        href="#full-artwork-details"
      >
        {{ additionalArtistsSubtitle }}
      </GuiLink>
      <GalleryLocation
        v-if="!humanMadeObject.isDeaccessioned"
        :human-made-object="humanMadeObject"
      />
      <div>
        <GuiLink
          class="a-title-and-artist__subtitle-full-details"
          href="#full-artwork-details"
        >
          {{ fullRecordDetails }}
        </GuiLink>
      </div>
    </div>
  </div>
</template>

<script>
// getty libraries
import {
  DelimitedFieldValues,
  GuiButton,
  GuiLink,
  useLocalization,
  scrollToHashMixin
} from "@thegetty-private/getty-ui";
// internal components/imports
import { GROUP_HEADING_LOCALE_KEY } from "@/helpers/HumanMadeObject";
import {
  sliceArrayOfArtistsForCaption,
  shouldSliceArrayOfArtistsForCaption,
  additionalArtistsSubtitle
} from "@/helpers/captionHelpers";
import GalleryLocation from "@/components/atoms/galleryLocation/index.vue";
import PageTitle from "@/components/atoms/pageTitle/index.vue";

export default {
  name: "TitleAndArtist",
  components: {
    DelimitedFieldValues,
    GalleryLocation,
    GuiButton,
    GuiLink,
    PageTitle
  },
  mixins: [scrollToHashMixin],
  props: {
    /**
     * The Human Made Object JSON-LD
     */
    humanMadeObject: {
      type: Object,
      required: true
    },
    /**
     * Whether to show fragment-type links for parts of a manuscript
     */
    showManuscriptLinks: {
      type: Boolean,
      required: false,
      default: undefined
    },
    /**
     * The orientation to display
     */
    orientation: {
      type: String,
      required: false,
      default: "horizontal",
      validator: (value) => ["horizontal", "vertical"].includes(value)
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * Gets full artwork details link label
       *
       * @returns {string}
       */
      fullRecordDetails: this.translatePart(
        "artist_and_title.fullRecordDetails"
      ),
      /**
       * Gets the translated text to display before the fragment parts link (if applicable)
       *
       * @returns {string}
       */
      fragmentPartsPrefix: this.translatePart(
        "artist_and_title.fragmentPartsPrefix"
      )
    };
  },
  computed: {
    /**
     * Gets the artist(s) from the store
     *
     * @returns {array}
     */
    artist() {
      return this.humanMadeObject?.objectProfile?.artists || [];
    },
    /**
     * Truncates artists if more than max to display
     *
     * @returns {array}
     */
    sliceArrayOfArtistsForCaption() {
      return sliceArrayOfArtistsForCaption(this.artist).map((a) => {
        return {
          ...a,
          // make sure the key is unique
          id: a.key
        };
      });
    },
    /**
     * The css classes for the page including the orientation
     */
    classes() {
      return [
        "a-title-and-artist__title",
        "a-title-and-artist__title--" + this.orientation
      ];
    },
    /**
     * The type of complex object (if applicable)
     *
     * @returns {string | undefined}
     */
    complexObjectType() {
      return this.humanMadeObject?.structure?.complexObjectType;
    },
    /**
     * Gets the creation date
     *
     * @returns {string}
     */
    creationDate() {
      return this.humanMadeObject?.objectProfile?.creationDate;
    },
    /**
     * The number of parts/children a (fragment) object has
     *
     * @returns {integer|undefined}
     */

    numberOfParts() {
      return this?.objectProfile?.numberOfChildren;
    },
    /**
     * Gets the page title
     *
     * @returns {string}
     */
    pageTitle() {
      return this.humanMadeObject?.objectProfile?.title;
    },
    /**
     * Whether to truncate list of artists
     *
     * @returns {bool}
     */
    shouldSliceArrayOfArtistsForCaption() {
      return shouldSliceArrayOfArtistsForCaption(this.artist);
    },
    /**
     * Gets more artists message if truncated
     *
     * @returns {string}
     */
    additionalArtistsSubtitle() {
      return additionalArtistsSubtitle(this.artistTotal, this.translatePart);
    },
    /**
     * Gets the total number of artists
     *
     * @returns {integer}
     */
    artistTotal() {
      return this.artist.length;
    },
    /**
     * Gets the translated link text for the fragment parts (if applicable)
     *
     * @returns {string}
     */
    fragmentPartsLink() {
      let numOfParts = this.humanMadeObject?.structure?.numberOfChildren;
      if (numOfParts) {
        return this.translateCount(
          "artist_and_title.fragmentPartsLink",
          numOfParts,
          {
            totalParts: this.localizeNumber(numOfParts)
          }
        );
      }
      return undefined;
    },
    /**
     * Whether to show the fragment parts link
     *
     * @returns {boolean}
     */
    showFragmentPartsLink() {
      if (
        (this.complexObjectType == "fragment" ||
          (this.complexObjectType == "set" &&
            !this.humanMadeObject?.structure?.partsHaveDistinctImages) ||
          (this.complexObjectType == "manuscript" &&
            this.showManuscriptLinks)) &&
        this.fragmentPartsLink
      ) {
        return true;
      }
      return false;
    },
    /**
     * The anchor to use when scrolling to the parts
     *
     * @return {string}
     */
    scrollToFragmentsAnchor() {
      let anchor = "#parts_of_group";
      if (
        this.humanMadeObject?.structure?.localeKey != GROUP_HEADING_LOCALE_KEY
      ) {
        anchor = "#parts_of_object";
      }
      return anchor;
    }
  },
  methods: {
    /**
     * Scrolls to fragment parts/parts of this object with sticky header offset
     */
    scrollToFragmentParts() {
      const stickyHeaderHeight = document.querySelector(
        ".o-hero-research-application__sticky-container"
      ).offsetHeight;
      this.scrollToHash(this.scrollToFragmentsAnchor, stickyHeaderHeight);
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
